import { useState, useEffect, useCallback } from 'react';

// Theme Hook - manages light/dark/system theme preference
const useTheme = () => {
  const [theme, setThemeState] = useState(() => localStorage.getItem('theme') || 'dark');

  const getEffective = useCallback((t) => {
    if (t === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return t;
  }, []);

  const [effectiveTheme, setEffectiveTheme] = useState(() => getEffective(theme));

  const applyTheme = useCallback((effective) => {
    document.documentElement.classList.remove('theme-light', 'theme-dark');
    document.documentElement.classList.add(effective === 'dark' ? 'theme-dark' : 'theme-light');
    setEffectiveTheme(effective);
  }, []);

  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : newTheme);
  }, [applyTheme]);

  useEffect(() => {
    applyTheme(getEffective(theme));
  }, [theme, applyTheme, getEffective]);

  // Listen for OS theme changes when in system mode
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (theme === 'system') {
        applyTheme(e.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme, applyTheme]);

  return { theme, effectiveTheme, setTheme };
};

export default useTheme;
